// /m/:slug — vanity-URL landing for billboard attribution.
//
// Logs the visit to /api/billboards/visit (public route), then redirects to
// the main landing page within ~400ms so the user feels no friction. The
// brief halo + market name acts as gentle confirmation they're in the right
// place ("Welcome from Tupelo, MS").

import { useEffect, useRef, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Static mirror of the backend MARKET_SLUGS — kept here so we can show the
// market name immediately without waiting for a roundtrip.
const KNOWN_SLUGS = {
  springfield: "Springfield, MO",
  branson:     "Branson, MO",
  joplin:      "Joplin, MO",
  tulsa:       "Tulsa, OK",
  okc:         "Oklahoma City, OK",
  jackson:     "Jackson, MS",
  tupelo:      "Tupelo, MS",
  birmingham:  "Birmingham, AL",
  montgomery:  "Montgomery, AL",
  knoxville:   "Knoxville, TN",
  chattanooga: "Chattanooga, TN",
  greenville:  "Greenville, SC",
  littlerock:  "Little Rock, AR",
  shreveport:  "Shreveport, LA",
  batonrouge:  "Baton Rouge, LA",
};

export default function MarketLanding() {
  const { slug = "" } = useParams();
  const [params] = useSearchParams();
  const [done, setDone] = useState(false);
  const logged = useRef(false);

  const cleanSlug = slug.toLowerCase().replace(/[^a-z0-9_]/g, "");
  const friendly = KNOWN_SLUGS[cleanSlug];
  const brand = (params.get("b") || "callgod").toLowerCase();

  useEffect(() => {
    if (logged.current) return;
    logged.current = true;
    // Fire-and-forget — we never block the user on attribution logging.
    try {
      fetch(`${API}/billboards/visit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slug: cleanSlug,
          brand,
          referrer: document.referrer || null,
        }),
        keepalive: true,
      }).catch(() => { /* ignore */ });
    } catch { /* ignore */ }
    // Hand the user off to the main landing after a brief beat.
    const t = setTimeout(() => setDone(true), friendly ? 700 : 250);
    return () => clearTimeout(t);
  }, [cleanSlug, brand, friendly]);

  if (done) return <Navigate to="/" replace />;

  return (
    <main
      data-testid="market-landing"
      data-slug={cleanSlug}
      className="flex min-h-screen items-center justify-center px-6"
    >
      <div className="cg-card w-full max-w-md rounded-2xl px-8 py-10 text-center">
        <div
          aria-hidden
          className="mx-auto h-16 w-16 rounded-full"
          style={{
            background:
              "radial-gradient(circle at center, hsla(41,75%,75%,0.85) 0%, hsla(41,75%,55%,0.35) 60%, transparent 100%)",
            filter: "blur(2px)",
          }}
        />
        <p className="mt-6 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          Welcome from
        </p>
        <h1
          className="font-serif-display mt-2 text-foreground"
          style={{ fontSize: "clamp(1.6rem, 5vw, 2.25rem)", letterSpacing: "-0.01em" }}
        >
          {friendly || "your billboard"}
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Opening the line…
        </p>
      </div>
    </main>
  );
}
